import { useCallback, useState } from "react";
import { translateText } from "@/api/translations";

export interface TranslationController {
  text: string;
  result: string | null;
  loading: boolean;
  rect: DOMRect | null;
  /** Translate `text` and anchor the popover to `rect`. */
  translate: (text: string, rect: DOMRect) => void;
  close: () => void;
}

/**
 * Inline-translation popover state for a reading. Pass the reading's
 * `languageId`; translation no-ops until it's known.
 */
export function useTranslation(languageId: number | undefined): TranslationController {
  const [text, setText] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const translate = useCallback(
    (selected: string, anchor: DOMRect) => {
      if (languageId === undefined) return;

      setText(selected);
      setRect(anchor);
      setResult(null);
      setLoading(true);
      translateText(languageId, selected)
        .then((res) => setResult(res.data.translation))
        .catch(() => setResult(null))
        .finally(() => setLoading(false));
    },
    [languageId],
  );

  const close = useCallback(() => {
    setText("");
    setRect(null);
    setResult(null);
  }, []);

  return { text, result, loading, rect, translate, close };
}
